import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Injector, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { uniqBy } from 'lodash-es';
import {
  catchError,
  combineLatest,
  EMPTY,
  finalize,
  map,
  Observable,
  of,
  take,
  withLatestFrom,
} from 'rxjs';

import {
  REST_API_CONNECTOR_TEMPLATE,
  RestApiConnectorDialogComponent,
} from '@blaze-dashboard/api-builder-feature';
import { BlazeTableService } from '@blaze-dashboard/blaze-table-feature';
import { ApiDef, ConnectorTarget, DatasetInfo } from '@blaze-shared/api-contracts-domain';
import { fromRouter } from '@blaze-shared/app-state-data-access';
import {
  ConnectorsApiService,
  ConnectorsCollectionService,
  ConnectorsTemplatesCollectionService,
} from '@blaze-shared/connectors-data-access';
import {
  ConnectorConfiguration,
  ConnectorCreationRequest,
  ConnectorOAuthConfig,
  ConnectorTemplate,
  FormValues,
} from '@blaze-shared/connectors-domain';
import {
  DatasetsApiService,
  GetPaginatedRequestModel,
  TablesApiService,
  TablesCollectionService,
} from '@blaze-shared/datasets-data-access';
import {
  BlazeTable,
  DEFAULT_BLAZE_TABLE,
  LinkedBlazeTableConnector,
} from '@blaze-shared/datasets-domain';
import { NotificationService } from '@blaze-shared/platform-data-access';
import { DatasetRowsService } from '@blaze-shared/schema-data-access';
import { DatasetRow } from '@blaze-shared/schema-domain';
import {
  ElementLoadingIndicatorComponent,
  PANEL_DATA,
  PanelComponent,
  PanelRef,
} from '@blaze-shared/shared-ui';

import { DataConnectorsStore } from '../+state/data-connectors.store';
import { DataConnectorDatasetsComponent } from '../data-connector-datasets/data-connector-datasets.component';
import { DataConnectorDetailsComponent } from '../data-connector-details/data-connector-details.component';
import { DatasetPreviewComponent } from '../dataset-preview/dataset-preview.component';
import { AircallConnectorDialogComponent } from '../dialogs/aircall-connector-dialog/aircall-connector-dialog.component';
import { AirtableConnectorDialogComponent } from '../dialogs/airtable-connector-dialog/airtable-connector-dialog.component';
import { BlazeTableDialogComponent } from '../dialogs/blaze-table-dialog/blaze-table-dialog.component';
import { CalConnectorDialogComponent } from '../dialogs/cal-connector-dialog/cal-connector-dialog.component';
import { CalendlyConnectorDialogComponent } from '../dialogs/calendly-connector-dialog/calendly-connector-dialog.component';
import { DropboxConnectorDialogComponent } from '../dialogs/dropbox-connector-dialog/dropbox-connector-dialog.component';
import { DropboxSignConnectorDialogComponent } from '../dialogs/dropbox-sign-connector-dialog/dropbox-sign-connector-dialog.component';
import { GenericOauthConnectorDialogComponent } from '../dialogs/generic-oauth-connector-dialog/generic-oauth-connector-dialog.component';
import { GoogleAnalyticsConnectorDialogComponent } from '../dialogs/google-analytics-connector-dialog/google-analytics-connector-dialog.component';
import { GoogleCalendarConnectorDialogComponent } from '../dialogs/google-calendar-connector-dialog/google-calendar-connector-dialog.component';
import { GoogleMailConnectorDialogComponent } from '../dialogs/google-mail-connector-dialog/google-mail-connector-dialog.component';
import { GoogleSheetsConnectorDialogComponent } from '../dialogs/google-sheets-connector-dialog/google-sheets-connector-dialog.component';
import { InBodyConnectorDialogComponent } from '../dialogs/in-body-connector-dialog/in-body-connector-dialog.component';
import { LifefileConnectorDialogComponent } from '../dialogs/lifefile-connector-dialog/lifefile-connector-dialog.component';
import { LinkedBlazeTableDialogComponent } from '../dialogs/linked-blaze-table-dialog/linked-blaze-table-dialog.component';
import { MailgunConnectorDialogComponent } from '../dialogs/mailgun-connector-dialog/mailgun-connector-dialog.component';
import { MicrosoftTeamsConnectorDialogComponent } from '../dialogs/microsoft-teams-connector-dialog/microsoft-teams-connector-dialog.component';
import { OneDriveConnectorDialogComponent } from '../dialogs/one-drive-connector-dialog/one-drive-connector-dialog.component';
import { OpenAIConnectorDialogComponent } from '../dialogs/openai-connector-dialog/openai-connector-dialog.component';
import { PioneerRxConnectorDialogComponent } from '../dialogs/pioneer-rx-connector-dialog/pioneer-rx-connector-dialog.component';
import { ScriptSureConnectorDialogComponent } from '../dialogs/scriptsure-connector-dialog/scriptsure-connector-dialog.component';
import { ShopifyConnectorDialogComponent } from '../dialogs/shopify-connector-dialog/shopify-connector-dialog.component';
import { StripeConnectorDialogComponent } from '../dialogs/stripe-connector-dialog/stripe-connector-dialog.component';
import { TwilioConnectorDialogComponent } from '../dialogs/twilio-connector-dialog/twilio-connector-dialog.component';
import { TypeformConnectorDialogComponent } from '../dialogs/typeform-connector-dialog/typeform-connector-dialog.component';
import {
  ConnectorDialogData,
  DialogPage,
  RenderMode,
  selfManagedConnectors,
} from '../models/data-connector-dialog';
import { ConnectorOAuthConfigs } from '../utils';

@Component({
  selector: 'blaze-data-connector-dialog',
  imports: [
    AircallConnectorDialogComponent,
    AirtableConnectorDialogComponent,
    AsyncPipe,
    BlazeTableDialogComponent,
    CalConnectorDialogComponent,
    CalendlyConnectorDialogComponent,
    DataConnectorDatasetsComponent,
    DataConnectorDetailsComponent,
    DatasetPreviewComponent,
    DropboxConnectorDialogComponent,
    DropboxSignConnectorDialogComponent,
    ElementLoadingIndicatorComponent,
    GenericOauthConnectorDialogComponent,
    GoogleAnalyticsConnectorDialogComponent,
    GoogleCalendarConnectorDialogComponent,
    GoogleMailConnectorDialogComponent,
    GoogleSheetsConnectorDialogComponent,
    InBodyConnectorDialogComponent,
    LifefileConnectorDialogComponent,
    LinkedBlazeTableDialogComponent,
    MailgunConnectorDialogComponent,
    MicrosoftTeamsConnectorDialogComponent,
    OneDriveConnectorDialogComponent,
    OpenAIConnectorDialogComponent,
    PanelComponent,
    PioneerRxConnectorDialogComponent,
    RestApiConnectorDialogComponent,
    ScriptSureConnectorDialogComponent,
    ShopifyConnectorDialogComponent,
    StripeConnectorDialogComponent,
    TwilioConnectorDialogComponent,
    TypeformConnectorDialogComponent,
  ],
  templateUrl: './data-connector-dialog.component.html',
  styleUrls: ['./data-connector-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DataConnectorsStore],
})
export class DataConnectorDialogComponent implements OnDestroy {
  private readonly blazeTableService = inject(BlazeTableService);
  private readonly connectorsApiService = inject(ConnectorsApiService);
  private readonly connectorsCollService = inject(ConnectorsCollectionService);
  private readonly connectorTemplatesCollService = inject(ConnectorsTemplatesCollectionService);
  private readonly dataConnectorsStore = inject(DataConnectorsStore);
  private readonly datasetRowsService = inject(DatasetRowsService);
  private readonly datasetsApiService = inject(DatasetsApiService);
  private readonly injector = inject(Injector);
  private readonly notificationService = inject(NotificationService);
  private readonly store = inject(Store);
  private readonly tablesApiService = inject(TablesApiService);
  private readonly tablesCollService = inject(TablesCollectionService);

  private readonly dialogData: ConnectorDialogData;
  private readonly dialogRef: MatDialogRef<any> | null;
  private readonly panelRef: PanelRef | null;
  private readonly renderMode: RenderMode;

  readonly DEFAULT_BLAZE_TABLE = DEFAULT_BLAZE_TABLE;
  readonly DialogPage = DialogPage;

  connector?: ConnectorConfiguration;
  connectorCreationRequest?: ConnectorCreationRequest;
  connectorOAuthConfig?: ConnectorOAuthConfig;
  connectorTarget?: ConnectorTarget;
  currPage: DialogPage = DialogPage.Undetermined;
  initialFormValues?: FormValues;
  linkedTableConnector?: LinkedBlazeTableConnector;
  previewData: DatasetRow[] = [];
  previewDataset?: DatasetInfo;
  previewTable?: BlazeTable;
  projectGroupId: string;
  table?: BlazeTable;
  template?: ConnectorTemplate;
  templates: ConnectorTemplate[] = [];

  coupledConnector$ = this.dataConnectorsStore.coupledConnector$;

  hasNecessaryDataBlazeTable$ = this.hasNecessaryData(DialogPage.BlazeTable);
  hasNecessaryDataBlazeTableLink$ = this.hasNecessaryData(DialogPage.LinkedBlazeTable);
  hasNecessaryDataCurrent$ = this.hasNecessaryData();
  hasNecessaryDataDatasets$ = this.hasNecessaryData(DialogPage.Datasets);
  hasNecessaryDataPreviewDatasets$ = this.hasNecessaryData(DialogPage.PreviewDataset);
  hasNecessaryDataPreviewTable$ = this.hasNecessaryData(DialogPage.PreviewTable);
  hasNecessaryDataRestApi$ = this.hasNecessaryData(DialogPage.RestApi);
  hasNecessaryDataSetup$ = this.hasNecessaryData(DialogPage.Setup);

  constructor() {
    const dialogData = this.injector.get(MAT_DIALOG_DATA, null);
    const panelData = this.injector.get(PANEL_DATA, null);
    this.dialogRef = this.injector.get(MatDialogRef, null);
    this.panelRef = this.injector.get(PanelRef, null);

    this.renderMode = this.dialogRef ? 'dialog' : 'panel';
    this.dialogData = dialogData || panelData;

    this.store
      .select(fromRouter.getProjectId)
      .pipe(take(1))
      .subscribe((projectId: string) => {
        if (projectId) {
          this.dataConnectorsStore.setProjectId(projectId);
        }
        this.init(this.dialogData);
      });

    this.connectorTemplatesCollService.entities$.subscribe((templates) => {
      this.templates = templates;
    });
  }

  ngOnDestroy(): void {
    this.dataConnectorsStore.destroy();
  }

  closeDialog(): void {
    this.dialogRef?.close();
    this.panelRef?.close();
  }

  showBlazeTablePage(table: BlazeTable): void {
    this.fetchPreviewData(undefined, table, undefined, false)?.subscribe((tableData) => {
      this.dataConnectorsStore.setBlazeTable(table);
      this.dataConnectorsStore.setBlazeTablePageToken(tableData.pageToken || null);
      this.dataConnectorsStore.setBlazeTableTotalCount(tableData.totalCount || null);
      this.dataConnectorsStore.setPreviewData(tableData.data);
      this.dataConnectorsStore.setCurrentPage(DialogPage.BlazeTable);
    });
  }

  showLinkedBlazeTablePage(): void {
    this.dataConnectorsStore.setCurrentPage(DialogPage.LinkedBlazeTable);
  }

  showDatasetsPage(connectorId?: string): void {
    if (connectorId) {
      this.connectorsApiService
        .getAllObjects(connectorId)
        .pipe(
          catchError((err) => {
            this.notificationService.showError(err);
            return EMPTY;
          }),
        )
        .subscribe((available) => {
          const datasets = available?.datasets?.toSorted() || [];
          const apiActions = available?.actions?.toSorted() || [];
          this.dataConnectorsStore.setAvailableApiActions(apiActions);
          this.dataConnectorsStore.setAvailableDatasets(datasets);
        });
    }
    this.dataConnectorsStore.setCurrentPage(DialogPage.Datasets);
  }

  showDatasetsPageForConnector(connector: ConnectorConfiguration): void {
    this.connector = connector;
    this.showDatasetsPage(connector.id);
  }

  showDatasetsPageForLinkedTable(connector: LinkedBlazeTableConnector): void {
    this.dataConnectorsStore.setAvailableApiActions([]);
    this.dataConnectorsStore.setAvailableDatasets([]);
    this.dataConnectorsStore.setSelectedDatasets([]);

    this.tablesApiService
      .getTablesForLinking(connector.projectId)
      .pipe(withLatestFrom(this.tablesCollService.entities$))
      .subscribe(([linkable, tables]) => {
        if (!linkable.length) {
          this.notificationService.error(`No candidates for linking in project ${connector.name}`);
          return;
        }
        const alreadyLinkedTables = tables.filter(
          (table) => table.projectId === connector.projectId,
        );
        this.linkedTableConnector = connector;
        this.dataConnectorsStore.setAvailableDatasets(linkable);
        this.dataConnectorsStore.setSelectedDatasets(
          uniqBy([...alreadyLinkedTables, ...connector.tables], (e) => e.id),
        );
        this.dataConnectorsStore.setCurrentPage(DialogPage.Datasets);
      });
  }

  showPage(page: DialogPage): void {
    this.dataConnectorsStore.setCurrentPage(page);
  }

  showRestApiPage(apiDefinition?: ApiDef | null): void {
    this.dataConnectorsStore.setCustomApiDefinition(apiDefinition);
    this.dataConnectorsStore.setCurrentPage(DialogPage.RestApi);
  }

  saveRestApiConnector(connector?: ConnectorConfiguration): void {
    if (connector) {
      this.connectorsCollService.upsertOneInCache(connector);
      if (this.panelRef) {
        this.panelRef.disableClose = false;
      }
      this.closeDialog();
    }
  }

  /**
   * Shows preview of the given dataset. Depending on the context (where preview button has been clicked), there
   * are 3 cases for fetching and displaying preview data (see cases below).
   * @param dataset
   */
  showPreviewPage(dataset?: DatasetInfo, table?: BlazeTable): void {
    this.dataConnectorsStore.formValues$.pipe(take(1)).subscribe((formValues) => {
      const previewData = this.fetchPreviewData(dataset, table, formValues);
      previewData?.pipe(take(1)).subscribe({
        next: (previewData) => {
          this.dataConnectorsStore.setPreviewData(previewData.data);
          if (dataset) {
            this.previewDataset = { ...dataset };
            this.dataConnectorsStore.setPreviewDataset(dataset);
            this.dataConnectorsStore.setCurrentPage(DialogPage.PreviewDataset);
          } else if (table) {
            this.previewTable = { ...table };
            this.dataConnectorsStore.setPreviewTable(table);
            this.dataConnectorsStore.setCurrentPage(DialogPage.PreviewTable);
          }
        },
        error: (error) => {
          this.notificationService.showError(error);
        },
      });
    });
  }

  /**
   * Navigates to previous dialog page. In case there was no previous page (new dialog was opened), dialog gets closed.
   */
  showPrevPage(): void {
    switch (this.currPage) {
      case DialogPage.Datasets:
        if (this.linkedTableConnector) {
          this.dataConnectorsStore.setCurrentPage(DialogPage.LinkedBlazeTable);
        } else {
          this.hasNecessaryDataSetup$.pipe(take(1)).subscribe((hasNecessaryData) => {
            const excludedTargets: ConnectorTarget[] = ['GoogleSheets', 'OneDrive'];
            const target = this.connector?.target;
            const canShowSetup = hasNecessaryData && target && !excludedTargets.includes(target);
            if (canShowSetup) {
              this.dataConnectorsStore.setCurrentPage(DialogPage.Setup);
            } else {
              this.closeDialog();
            }
          });
        }
        break;
      case DialogPage.PreviewDataset:
        this.hasNecessaryDataDatasets$.pipe(take(1)).subscribe((canShowDatasets) => {
          if (canShowDatasets) {
            this.dataConnectorsStore.setCurrentPage(DialogPage.Datasets);
          } else {
            this.closeDialog();
          }
        });
        break;
      default:
        this.closeDialog();
    }
  }

  showSetupPage(): void {
    this.dataConnectorsStore.setCurrentPage(DialogPage.Setup);
  }

  /**
   * Checks whether we have necessary data to display given dialog page. Pages which cannot have necessary data
   * cannot be rendered and are hidden behind *ngIf directive.
   * @param page - If not specified, current page will be used.
   * @returns
   */
  hasNecessaryData(page?: DialogPage): Observable<boolean> {
    return combineLatest([
      this.dataConnectorsStore.availableDatasets$,
      this.dataConnectorsStore.availableApiActions$,
      this.dataConnectorsStore.previewData$,
      this.dataConnectorsStore.previewTable$,
      this.dataConnectorsStore.currentPage$,
      this.dataConnectorsStore.blazeTable$,
      this.dataConnectorsStore.customApiDefinition$,
      this.dataConnectorsStore.loadingDatasets$,
    ]).pipe(
      map(
        ([
          availableDatasets,
          availableApiActions,
          previewData,
          previewTable,
          currentPage,
          blazeTable,
          customApiDefinition,
          loadingDatasets,
        ]) => {
          switch (page || currentPage) {
            case DialogPage.BlazeTable:
              return !!blazeTable;
            case DialogPage.LinkedBlazeTable:
              return !!this.linkedTableConnector;
            case DialogPage.Setup:
              return !!this.template?.id && this.template.id !== REST_API_CONNECTOR_TEMPLATE.id;
            case DialogPage.Datasets:
              return !!(availableDatasets?.length || availableApiActions?.length);
            case DialogPage.PreviewDataset:
              return !!previewData && !loadingDatasets;
            case DialogPage.PreviewTable:
              return !!previewTable;
            case DialogPage.RestApi:
              return (
                this.template?.id === REST_API_CONNECTOR_TEMPLATE.id ||
                customApiDefinition !== undefined
              );
            case DialogPage.ConnectorWizard:
              return true;
            default:
              return false;
          }
        },
      ),
    );
  }

  /**
   * Used to facilitate previous/next page navigation. Non-current pages are simply hidden from view and showed
   * when user navigates to them. This is separate from hasNecessaryData() function in that page which doesn't
   * fullfil hasNecessaryData conditions cannot be rendered at all and is hidden behind *ngIf directive.
   * @param page
   * @returns
   */
  isHidden(page: DialogPage): boolean {
    return this.currPage !== page;
  }

  /**
   * Used to check if dialog should render custom connector pages.
   * It is used together with hasNecessaryData.
   * @param page
   * @param connectorTarget
   */
  isOnPage(page: DialogPage, connectorTarget?: ConnectorTarget): boolean {
    const matchesPage = this.currPage === page;
    if (!connectorTarget) {
      return matchesPage;
    }
    return matchesPage && this.connectorTarget === connectorTarget;
  }

  isConnectorSelfManaged(target: ConnectorTarget | undefined): boolean {
    if (!target) {
      return false;
    }
    return selfManagedConnectors.has(target);
  }

  isGenericOAuthConnector(target: ConnectorTarget | undefined): boolean {
    if (!target) {
      return false;
    }
    return !!ConnectorOAuthConfigs[target];
  }

  refreshTableData(table: BlazeTable): void {
    table = table ?? this.table;
    if (!table) {
      return;
    }

    this.showBlazeTablePage(table);
  }

  refreshDataset(): void {
    if (!this.previewDataset?.id) {
      return;
    }

    this.datasetsApiService.postRefresh(this.previewDataset?.id).subscribe((dataset) => {
      this.showPreviewPage(dataset);
    });
  }

  private init(dialogData: ConnectorDialogData): void {
    const connectorTarget = dialogData.connector?.target ?? dialogData.template?.target;

    this.connector = dialogData.connector; // Defined for configured connectors
    this.connectorCreationRequest = dialogData.connectorCreationRequest;
    this.connectorTarget = connectorTarget;
    this.connectorOAuthConfig = ConnectorOAuthConfigs[connectorTarget ?? ''];
    this.linkedTableConnector = dialogData.linkedTableConnector;
    this.previewDataset = dialogData.previewDataset;
    this.previewTable = dialogData.previewTable;
    this.projectGroupId = dialogData.linkedTableConnector?.projectGroupId ?? '';
    this.table = dialogData.table;
    this.template = dialogData.template; // Defined for creating new connector

    // Route to appropriate page based on the received data.
    switch (dialogData.action) {
      case 'create':
        this.routeCreateAction(dialogData);
        break;
      case 'editConnector':
        this.routeEditConnectorAction(dialogData);
        break;
      case 'editDatasets':
        this.routeEditDatasetsAction(dialogData);
        break;
      case 'preview':
        this.routePreviewAction(dialogData);
        break;
      default:
        console.error('No action was specified for dialog data', dialogData);
    }

    this.dataConnectorsStore.previewData$.subscribe(
      (previewData) => (this.previewData = previewData),
    );
    this.dataConnectorsStore.currentPage$.subscribe((currentPage) => (this.currPage = currentPage));
    this.dataConnectorsStore.setSelectedDatasets(this.connector?.datasets || []);
    this.dataConnectorsStore.setConnector(this.connector ?? null);
  }

  private routeCreateAction(data: ConnectorDialogData): void {
    this.initialFormValues = undefined;
    this.connector = undefined;
    if (data.table) {
      this.showBlazeTablePage(data.table);
    } else if (data.template?.id === REST_API_CONNECTOR_TEMPLATE.id) {
      this.showRestApiPage();
    } else if (this.isConnectorSelfManaged(data.template?.target)) {
      this.showPage(DialogPage.ConnectorWizard);
    } else if (this.linkedTableConnector) {
      this.showLinkedBlazeTablePage();
    } else if (data.template?.id) {
      this.showSetupPage();
    }
  }

  private routeEditConnectorAction(data: ConnectorDialogData): void {
    if (data.connector?.target?.includes('Airtable')) {
      this.template = undefined;
      this.showPage(DialogPage.ConnectorWizard);
    } else if (this.isConnectorSelfManaged(data.connector?.target)) {
      this.showPage(DialogPage.ConnectorWizard);
    } else if (data.table) {
      this.showBlazeTablePage(data.table);
    } else if (data.template?.id === REST_API_CONNECTOR_TEMPLATE.id) {
      this.showRestApiPage(data.connector?.customApiDefinition);
    } else if (this.linkedTableConnector?.projectId) {
      this.showDatasetsPageForLinkedTable(this.linkedTableConnector);
    } else if (data.connector?.isEditable && data.connector?.id && !data.previewDataset) {
      this.initialFormValues = {
        name: data.connector.name,
        ...data.connector.configuration,
      };
      this.showSetupPage();
    }
  }

  private routeEditDatasetsAction(data: ConnectorDialogData): void {
    if (data.connector?.connectorType !== 'Api' && data.connector?.id && !data.previewDataset) {
      this.showDatasetsPage(data.connector.id);
    }
  }

  private routePreviewAction(data: ConnectorDialogData): void {
    if (data.previewDataset) {
      this.showPreviewPage(data.previewDataset);
    }
  }

  private fetchPreviewData(
    dataset?: DatasetInfo,
    table?: BlazeTable,
    formValues?: FormValues,
    useSort = true,
  ): Observable<{
    data: DatasetRow[];
    pageToken?: string | null;
    totalCount?: number | null;
  }> | null {
    const id = dataset?.id || table?.id || '';
    let previewData$: Observable<{
      data: DatasetRow[];
      pageToken?: string | null;
      totalCount?: number | null;
    }>;
    if (id) {
      // Case 1: Show preview of the configured dataset for the given connector. This happens when we initiate
      // preview of the dataset from side menu.
      if (table) {
        const requestData: GetPaginatedRequestModel = {
          pagination: {
            maxCount: 50,
            returnTotalCount: true,
          },
          filter: null,
        };
        if (useSort) {
          requestData.sort = {
            columnName: table.displayColumn || this.blazeTableService.prepareColumns(table)[0].name,
            direction: 'Ascending',
          };
        }
        previewData$ = this.datasetsApiService.postGetPaginated(id, requestData, false).pipe(
          map((res) => ({
            data: res.data,
            pageToken: res.pagination.nextPageToken,
            totalCount: res.pagination.totalCount,
          })),
        );
      } else {
        // For non-blaze table connectors, fetch only first 50 results.
        previewData$ = this.datasetRowsService
          .get({ datasetId: id, maxResults: 50, includeRowId: false })
          .pipe(map((data) => ({ data })));
      }
    } else if (dataset?.name && this.connector?.id) {
      // Case 2: Show preview of a dataset that can be added to the already-configured connector.
      // This happens when we initiate preview when editing a configured connector.
      previewData$ = this.connectorsApiService
        .postSample({
          connectorId: this.connector?.id,
          datasetName: dataset?.name,
        })
        .pipe(map((data) => ({ data })));
    } else if (this.template?.id && dataset?.name && !this.connector?.id) {
      // Case 3: Show preview of a dataset that can be added to connector we're attempting to create. This happens
      // when we initiate preview when adding a new connector.
      previewData$ = this.connectorsApiService
        .postSample({
          configuration: formValues,
          datasetName: dataset?.name,
          templateId: this.template.id,
        })
        .pipe(map((data) => ({ data })));
    } else {
      previewData$ = of({ data: [] });
    }

    if (!previewData$) {
      return null;
    }

    this.dataConnectorsStore.setLoadingDatasets(true);
    return previewData$.pipe(
      finalize(() => {
        this.dataConnectorsStore.setLoadingDatasets(false);
      }),
    );
  }
}
